import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useMobileMenu } from '../context/MobileMenuContext';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [dropdownClosing, setDropdownClosing] = useState(false);
  const { mobileMenuOpen, toggleMobileMenu, closeMobileMenu } = useMobileMenu();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation('common');
  const location = useLocation();
  const productsWrapperRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDropdownLinkClick = (closeMobileFn) => {
    // Briefly disable hover to force-close CSS :hover mega dropdowns on click
    setDropdownClosing(true);
    setTimeout(() => setDropdownClosing(false), 300);
    document.activeElement?.blur();
    closeMobileMenu();
    if (closeMobileFn) closeMobileFn();
  };

  const navLinks = [
    { path: `/${language}`, label: t('nav.home') },
    // { path: `/${language}/capabilities`, label: t('nav.capabilities') },
    { path: `/${language}/platform`, label: t('nav.platform') },
  ];

  const postDropdownLinks = [
    { path: `/${language}/industries`, label: t('nav.industries') },
    { path: `/${language}/clientele`, label: t('nav.clientele') },
    { path: `/${language}/about`, label: t('nav.about') },
  ];

  const productItems = [
    {
      path: `/${language}/ai-governance`,
      label: t('nav.mega.aiSolutions.items.aiGovernance.label'),
      tag: t('nav.mega.aiSolutions.items.aiGovernance.tag'),
      description: t('nav.productsDesc.aiGovernance'),
    },
    {
      path: `/${language}/enterprise-ai`,
      label: t('nav.mega.aiSolutions.items.enterpriseAI.label'),
      tag: t('nav.mega.aiSolutions.items.enterpriseAI.tag'),
      description: t('nav.productsDesc.enterpriseAI'),
    },
    {
      path: `/${language}/knowledge-hub`,
      label: t('nav.mega.aiSolutions.items.knowledgeHub.label'),
      tag: t('nav.mega.aiSolutions.items.knowledgeHub.tag'),
      description: t('nav.productsDesc.knowledgeHub'),
    },
    {
      path: `/${language}/ai-agents`,
      label: t('nav.mega.aiSolutions.items.aiAgents.label'),
      tag: t('nav.mega.aiSolutions.items.aiAgents.tag'),
      description: t('nav.productsDesc.aiAgents'),
    },
    {
      path: `/${language}/ai-investigation`,
      label: t('nav.mega.aiSolutions.items.aiInvestigation.label'),
      tag: t('nav.mega.aiSolutions.items.aiInvestigation.tag'),
      description: t('nav.productsDesc.aiInvestigation'),
    },
    {
      path: `/${language}/e1-databridge`,
      label: t('nav.mega.aiSolutions.items.aiDataBridge.label'),
      tag: t('nav.mega.aiSolutions.items.aiDataBridge.tag'),
      description: t('nav.productsDesc.aiDataBridge'),
    },
  ];

  const isProductActive = productItems.some(
    (item) => location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  );

  const MegaDropdown = ({ items, title, subtitle }) => (
    <div className="mega-dropdown">
      <div className="mega-dropdown-inner">
        <div className="mega-dropdown-header">
          <span className="mega-dropdown-title">{title}</span>
          <span className="mega-dropdown-subtitle">{subtitle}</span>
        </div>
        <div className="mega-dropdown-grid">
          {items.map((item) => {
            const isCardActive = location.pathname === item.path
              || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`mega-dropdown-card${isCardActive ? ' active' : ''}`}
                onClick={() => handleDropdownLinkClick()}
              >
                <div className="mega-card-body">
                  <span className="mega-card-tag">{item.tag}</span>
                  <span className="mega-card-name">{item.label}</span>
                  <span className="mega-card-desc">{item.description}</span>
                </div>
                <i className="fas fa-arrow-right mega-card-arrow"></i>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );

  const MobileAccordion = ({ items, label, isActive, isOpen, onToggle }) => (
    <div className="mobile-products-accordion">
      <button
        className={`nav-link mobile-products-toggle${isActive ? ' active' : ''}`}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        {label}
        <i className={`fas fa-chevron-down mobile-products-chevron ${isOpen ? 'rotated' : ''}`}></i>
      </button>
      <div className={`mobile-products-list ${isOpen ? 'open' : ''}`}>
        {items.map((item) => {
          const isCardActive = location.pathname === item.path
            || location.pathname.startsWith(item.path + '/');
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`mobile-product-item${isCardActive ? ' active' : ''}`}
              onClick={() => handleDropdownLinkClick(onToggle)}
            >
              {item.label}
            </Link>
          );
        })}
      </div>
    </div>
  );

  return (
    <header className={`site-header ${scrolled ? 'scrolled' : ''}`}>
      <div className="container-fluid">
        <div className="header-content">
          {/* Logo */}
          <Link to="/" className="logo-link">
            <img
              src="/Images/logo-white.png"
              alt="EWANDS"
              className={`logo ${theme === 'light' ? 'logo-light-theme' : ''}`}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            {navLinks.map((link, index) => (
              <NavLink
                key={link.path}
                to={link.path}
                end={index === 0}
                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
              >
                {link.label}
              </NavLink>
            ))}

            <NavLink
              to={`/${language}/applications`}
              className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
            >
              {t('nav.solutions')}
            </NavLink>

            {/* Products mega dropdown */}
            <div className={`products-dropdown-wrapper${dropdownClosing ? ' dropdown-closing' : ''}`} ref={productsWrapperRef} tabIndex={-1}>
              <button
                className={`nav-link products-trigger${isProductActive ? ' active' : ''}`}
                aria-haspopup="true"
              >
                {t('nav.products')}
                <i className="fas fa-chevron-down products-chevron"></i>
              </button>
              <MegaDropdown
                items={productItems}
                title={t('nav.mega.aiSolutions.title')}
                subtitle={t('nav.mega.aiSolutions.subtitle')}
              />
            </div>

            {postDropdownLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Right Section */}
          <div className="header-right">
            <Link to={`/${language}/contact`} className="customer-service-btn" aria-label="Contact Us">
              <i className="fas fa-location-dot"></i>
            </Link>
            <div className="header-desktop-toggles">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
            <button
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <i className={`fas fa-${mobileMenuOpen ? 'times' : 'bars'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-nav-inner">
            {navLinks.map((link, index) => (
              <NavLink
                key={link.path}
                to={link.path}
                end={index === 0}
                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                onClick={closeMobileMenu}
              >
                {link.label}
              </NavLink>
            ))}

            <NavLink
              to={`/${language}/applications`}
              className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
              onClick={closeMobileMenu}
            >
              {t('nav.solutions')}
            </NavLink>

            <MobileAccordion
              items={productItems}
              label={t('nav.products')}
              isActive={isProductActive}
              isOpen={mobileProductsOpen}
              onToggle={() => setMobileProductsOpen((prev) => !prev)}
            />

            {postDropdownLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                onClick={closeMobileMenu}
              >
                {link.label}
              </NavLink>
            ))}

            <NavLink
              to={`/${language}/contact`}
              className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
              onClick={closeMobileMenu}
            >
              {t('nav.contact')}
            </NavLink>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
